import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Skeleton } from '@/components/Skeleton/Skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableNum,
    TableRow,
} from '@/components/Table/Table';
import { taxExportUrl, type TaxPeriodRow } from '@/features/reports/api';
import { TAX_COPY as COPY } from '@/features/reports/copy';
import { PeriodControl, ReportCard, ReportFailure, Tile } from '@/features/reports/parts';
import { type ReportPeriod, TAX_PERIODS, taxPeriod } from '@/features/reports/periods';
import { reportTaxQuery } from '@/features/reports/queries';
import { ExportButton } from '@/features/reports/transactions/ExportButton';
import { taxExportFileName } from '@/features/reports/transactions/labels';
import { formatMoney } from '@/lib/format';
import { cn } from '@/util/utils';

/** Tax figures to the penny: they are what finance files, so nothing is rounded away. */
const money = (pounds: number) => formatMoney(pounds, 'GBP', 2);

const sum = (periods: TaxPeriodRow[], pick: (p: TaxPeriodRow) => number) =>
    periods.reduce((total, p) => total + pick(p), 0);

/**
 * Transactions › Tax (ALS-90): VAT, refunded VAT, net taxable and duties by filing period, every
 * figure the issued invoices' and credit notes', never the orders' worked out again. VAT and
 * duties stay apart, as the prototype keeps them: two obligations, settled to two places.
 *
 * The period is the page's shared one, read as the cycles VAT is filed on: a Day or Week chosen on
 * another tab shows as Month here. Choosing a period in the table shows it by tax register row
 * below (Zero rated, VAT 20%, Margin scheme), the latest first.
 */
export function TaxView({
    period,
    onPeriodChange,
}: {
    period: ReportPeriod;
    onPeriodChange: (period: ReportPeriod) => void;
}) {
    const cycle = taxPeriod(period);
    const report = useQuery(reportTaxQuery(cycle));
    const data = report.data;
    const [chosen, setChosen] = useState<string | null>(null);

    const periods = data?.periods ?? [];
    const selected = periods.find((p) => p.from === chosen) ?? periods.at(-1);
    // Labels and the export follow the cycle the figures were built for.
    const shown = data?.period ?? cycle;

    return (
        <>
            <PeriodControl
                period={cycle}
                onPeriodChange={onPeriodChange}
                periods={TAX_PERIODS}
                label={COPY.period}
            />

            {/* A new cycle shows this until its own figures arrive, never the last cycle's
                figures under it (ALS-476). */}
            {report.isPending ? (
                <div
                    aria-busy="true"
                    aria-label="Loading the report"
                    className="flex flex-col gap-(--layout-row-gap)"
                >
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        {Array.from({ length: 4 }, (_, i) => (
                            <Skeleton key={i} className="h-[84px] rounded-lg" />
                        ))}
                    </div>
                    <Skeleton className="h-[320px] rounded-md" />
                </div>
            ) : report.isError && !data ? (
                <ReportFailure copy={COPY.failed} onRetry={() => void report.refetch()} />
            ) : data ? (
                <div className="flex flex-col gap-(--layout-row-gap)">
                    {/* Totals to the penny run to "£1,249,717.58", wider than half a phone, so the
                        tiles stand one to a row until there is room for two. */}
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <Tile
                            label={COPY.kpis.netSales(periods.length, shown)}
                            value={money(sum(periods, (p) => p.netSales))}
                            note={COPY.kpiNotes.netSales}
                        />
                        <Tile
                            label={COPY.kpis.vat}
                            value={money(sum(periods, (p) => p.vat))}
                            note={COPY.kpiNotes.vat}
                        />
                        <Tile
                            label={COPY.kpis.duties}
                            value={money(sum(periods, (p) => p.duties))}
                            note={COPY.kpiNotes.duties}
                        />
                        <Tile
                            label={COPY.kpis.totalDue}
                            value={money(sum(periods, (p) => p.totalDue))}
                            note={COPY.kpiNotes.totalDue}
                        />
                    </div>

                    <ReportCard
                        title={COPY.reconciliation.title}
                        subtitle={COPY.reconciliation.subtitle}
                        actions={
                            <ExportButton
                                url={taxExportUrl(shown)}
                                fileName={taxExportFileName(shown)}
                            />
                        }
                    >
                        {/* Periods against six measures is a matrix, so a phone scrolls it
                            sideways in a labelled region, as Margin's does, rather than stacking
                            it into cards nobody can compare. */}
                        <Table region={COPY.reconciliation.title}>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{COPY.reconciliation.columns.period}</TableHead>
                                    <TableHead className="text-right">
                                        {COPY.reconciliation.columns.netSales}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {COPY.reconciliation.columns.vat}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {COPY.reconciliation.columns.refundedVat}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {COPY.reconciliation.columns.netTaxable}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {COPY.reconciliation.columns.duties}
                                    </TableHead>
                                    <TableHead className="text-right">
                                        {COPY.reconciliation.columns.totalDue}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {periods.map((p) => {
                                    const isSelected = p === selected;
                                    return (
                                        <TableRow
                                            key={p.from}
                                            data-state={isSelected ? 'selected' : undefined}
                                        >
                                            <TableCell className="whitespace-nowrap">
                                                {/* A button, so choosing a period by keyboard or
                                                    touch is the same as by pointer; a finger gets
                                                    the 44px target on any width, a tablet too. */}
                                                <button
                                                    type="button"
                                                    aria-pressed={isSelected}
                                                    onClick={() => setChosen(p.from)}
                                                    className="focus-visible:outline-ring -mx-1 rounded-sm px-1 text-left font-medium outline-none hover:underline focus-visible:outline-2 focus-visible:outline-solid pointer-coarse:min-h-11"
                                                >
                                                    {p.label}
                                                </button>
                                            </TableCell>
                                            <TableNum>{money(p.netSales)}</TableNum>
                                            <TableNum>{money(p.vat)}</TableNum>
                                            <TableNum>{money(p.refundedVat)}</TableNum>
                                            <TableNum>{money(p.netTaxable)}</TableNum>
                                            <TableNum>{money(p.duties)}</TableNum>
                                            <TableNum
                                                className={cn(
                                                    'font-medium',
                                                    p.totalDue < 0 && 'text-destructive',
                                                )}
                                            >
                                                {money(p.totalDue)}
                                            </TableNum>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </ReportCard>

                    {selected ? <TaxRows period={selected} /> : null}
                </div>
            ) : null}
        </>
    );
}

/** One period by the tax register's rows, the ticket's grouping (Franka, 21 Sep 2026). */
function TaxRows({ period }: { period: TaxPeriodRow }) {
    const columns = COPY.rows.columns;
    return (
        <ReportCard title={COPY.rows.title} subtitle={COPY.rows.subtitle(period.label)}>
            {period.rows.length === 0 ? (
                <p className="text-muted-foreground px-1 py-6 text-center text-sm">
                    {COPY.rows.empty(period.label)}
                </p>
            ) : (
                <Table region={COPY.rows.title}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{columns.taxRow}</TableHead>
                            <TableHead className="text-right">{columns.net}</TableHead>
                            <TableHead className="text-right">{columns.vat}</TableHead>
                            <TableHead className="text-right">{columns.refundedNet}</TableHead>
                            <TableHead className="text-right">{columns.refundedVat}</TableHead>
                            <TableHead className="text-right">{columns.netTaxable}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {period.rows.map((r) => (
                            <TableRow key={r.taxRow}>
                                <TableCell className="font-medium whitespace-nowrap">
                                    {r.taxRow}
                                </TableCell>
                                <TableNum>{money(r.net)}</TableNum>
                                <TableNum>{money(r.vat)}</TableNum>
                                <TableNum>{money(r.refundedNet)}</TableNum>
                                <TableNum>{money(r.refundedVat)}</TableNum>
                                <TableNum className="font-medium">{money(r.netTaxable)}</TableNum>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </ReportCard>
    );
}
